/**
 * useEpisodeReadyVoice
 *
 * Registers the EpisodePage "ready" screen with the persistent HandsFreeProvider:
 * reads the situation / character / role aloud (TTS), then listens for "começar".
 * The provider handles read→listen, persistence across navigation, and the mic
 * handoff to the conversation (when `active` goes false on status change).
 */

import { useMemo } from 'react';
import type { ScenarioData } from '../services/GameApiService';
import { useHandsFreeScreen } from './useHandsFreeScreen';
import { buildEpisodeReadyReadText } from './navPrompts';
import { EPISODE_READY_TOOLS, NAV_TOOL } from './navTools';

interface UseEpisodeReadyVoiceArgs {
  enabled: boolean;
  token: string;
  scenarioData: ScenarioData | null;
  playerRole: string;
  /** Episode 1 reads the opener situation; later episodes read the bridge on the waiting screen. */
  isFirstEpisode: boolean;
  /** Existing startPlaying handler from EpisodePage. */
  onStart: () => void;
}

export function useEpisodeReadyVoice({
  enabled,
  token,
  scenarioData,
  playerRole,
  isFirstEpisode,
  onStart,
}: UseEpisodeReadyVoiceArgs): void {
  const readText = useMemo(() => {
    if (!scenarioData) return '';
    return buildEpisodeReadyReadText({
      title: scenarioData.title,
      situation: isFirstEpisode ? (scenarioData.frontendSituation || scenarioData.description) : undefined,
      characterName: scenarioData.characterName,
      aiRole: scenarioData.aiRole,
      characterDescription: scenarioData.playerFacingDescription || scenarioData.characterDescription,
      playerRole: playerRole || scenarioData.userRole,
    });
  }, [scenarioData, playerRole, isFirstEpisode]);

  useHandsFreeScreen({
    active: enabled && !!scenarioData,
    token,
    readKey: scenarioData ? `episode-ready:${scenarioData.title}` : '',
    readText,
    tools: EPISODE_READY_TOOLS,
    onCommand: (name) => {
      if (name === NAV_TOOL.CONFIRM_START) onStart();
    },
  });
}
